import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  allFeatureSubscriptionTypes,
  proFeatureSubscriptionTypes,
  SUBSCRIPTION_TYPES,
} from 'src/app/models/payment';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs/operators';
import { filterNil } from '@ngneat/elf';
import { MarioResource } from '../../../models/session';

export const APP_DEFAULT_WIDTH = 1280;
export const APP_DEFAULT_HEIGHT = 720;

export const MARIO_LARGE_WIDTH = 960;
export const MARIO_LARGE_HEIGHT = 540;

export const INTERACTIONS_BAR_HEIGHT = 46;
export const INTERACTIONS_BAR_PADDING = 1;
export const TOP_CONTROLS_V1_MOBILE_HEIGHT = 42;

export const WEB_VIEWER_PADDING = 4;

// those limits are currently applied to videos, remote videos, iframes, web viewer and chat-GPT
export const APPS_MIN_WIDTH = 480;
export const APPS_MIN_HEIGHT = 480;
export const APPS_MAX_WIDTH = 1920;
export const APPS_MAX_HEIGHT = 1080;

export const CHAT_GPT_APP_DEFAULT_WIDTH = 549;
export const CHAT_GPT_APP_DEFAULT_HEIGHT = 620;
export const CHAT_GPT_MIN_WIDTH = 360;
export const CHAT_GPT_MIN_HEIGHT = 360;

export const MARIO_DEFAULT_WIDTH = 700;

export const MARIO_DEFAULT_HEIGHT =
  394 /* maintain aspect ratio for height = 700 */ + INTERACTIONS_BAR_HEIGHT;

export const REMOTE_VIDEO_DEFAULT_WIDTH = 1280;
export const REMOTE_VIDEO_DEFAULT_HEIGHT = 720;

export const VIDEO_DEFAULT_WIDTH = 1280;
export const VIDEO_DEFAULT_HEIGHT = 720;

export const QUESTIONS_DEFAULT_WIDTH = 800;
export const QUESTIONS_DEFAULT_HEIGHT = 600;

// Assuming a PDF of the standard US letter size 8.5 x 11 inch at 100 DPI
export const PDF_PAGE_DEFAULT_HEIGHT = 1100;
export const PDF_PAGE_DEFAULT_WIDTH = 850;

export const IMAGES_DEFAULT_WIDTH = 600;
export const IMAGES_DEFAULT_HEIGHT = 600;

export const FORMULAS_DEFAULT_HEIGHT = 200;
export const FORMULAS_DEFAULT_WIDTH = 200;

export enum AppSections {
  CORE = 'Core',
  PRODUCTIVITY = 'Productivity',
  AI = 'AI-Enabled Apps',
  EDUCATION = 'General Learning Tools',
  MATH = 'Numeracy and Math Tools',
  LITERACY = 'Literacy and Language Tools',
  SCIENCE = 'Science Tools',
  MUSIC = 'Music Tools',
  LMS = 'Learning Portals - Account Required',
  DESIGN = 'Design',
  GAMES = 'Games',
  CODING = 'Coding Tools',
  ADVANCED = 'Advanced',
  APPS_ONLY_VISIBLE_TO_YOU = 'Apps only visible to you',
  OTHERS = 'Other tools',
  REQUEST_AN_APP = 'Request an app',
}

export interface AppSectionsLocalized {
  value: AppSections;
  label: string;
}

export enum CollaborativeApps {
  // Core apps
  COMMENTS = 'COMMENTS',
  ANALYTICS = 'ANALYTICS',
  BREAKOUT_ROOMS = 'BREAKOUT_ROOMS',
  RESOURCE_LIBRARY = 'RESOURCE_LIBRARY',
  QUESTION_LIBRARY = 'QUESTION_LIBRARY',

  // Google apps
  GOOGLE_DOCS = 'GOOGLE_DOCS',
  GOOGLE_SHEETS = 'GOOGLE_SHEETS',
  GOOGLE_SLIDES = 'GOOGLE_SLIDES',
  GOOGLE_FORMS = 'GOOGLE_FORMS',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',

  // Microsoft apps
  ONEDRIVE = 'ONEDRIVE',

  // IFrame apps
  IFRAME = 'IFRAME',

  AVALON = 'AVALON',
  CODE_WORDS = 'CODE_WORDS',
  COLONIST = 'COLONIST',
  DOWN_FOR_A_CROSS = 'DOWN_FOR_A_CROSS',
  DRAW_BATTLE = 'DRAW_BATTLE',
  LI_POKER = 'LI_POKER',
  SCATTEGORIES = 'SCATTEGORIES',
  SKRIBBLE_IO = 'SKRIBBLE_IO',
  TOP_FOUR = 'TOP_FOUR',
  WEREWOLF = 'WEREWOLF',
  WORDLE = 'WORDLE',

  // Mario apps
  POLLS = 'ia-polls',
  STOPWATCH = 'ia-stopwatch',
  TIMER = 'ia-timer',
  ERASER = 'ia-eraser',
  YOUTUBE_MARIO = 'ia-yt',
  DICE_MARIO = 'ia-dice',
  MARIO_IFRAME = 'ia-iframe',

  // Mario iFrame apps
  KAHOOT = 'KAHOOT',
  NEARPOD = 'NEARPOD',

  // Webviewer bookmarks
  QUIZLET = 'QUIZLET',
  QUIZIZZ = 'QUIZIZZ',
  MARIO = 'MARIO',
  WEBVIEWER = 'WEBVIEWER',

  // AI Enabled Apps
  CHAT_GPT = 'CHAT_GPT',
  PENCIL_AI = 'PENCIL_AI',
  PENCIL_TEMPLATE = 'PENCIL_TEMPLATE',
  PENCIL_AI_ASSISTANT = 'PENCIL_AI_ASSISTANT',

  // LMS
  ABC_READING_EGGS = 'ABC_READING_EGGS',
  ACTIVELEARN = 'ACTIVELEARN',
  BLACKBOARD = 'BLACKBOARD',
  CANVAS_LMS = 'CANVAS_LMS',
  GOOGLE_CLASSROOM_LMS = 'GOOGLE_CLASSROOM_LMS',
  IREADY = 'IREADY',
  IXL = 'IXL',
  OFF2CLASS = 'OFF2CLASS',
  MATHIA = 'MATHIA',
  MYMATHS = 'MYMATHS',
  XCELLATE = 'XCELLATE',
  GEOGEBRA = 'GEOGEBRA',
  WORDWALL = 'WORDWALL',
  READWORKS = 'READWORKS',

  // Math tools
  CLASSPAD = 'CLASSPAD',
  DESMOS_GRAPHING_CALCULATOR = 'DESMOS_GRAPHING_CALCULATOR',
  DESMOS_GEOMETRY = 'DESMOS_GEOMETRY',
  DESMOS_SCIENTIFIC_CALCULATOR = 'DESMOS_SCIENTIFIC_CALCULATOR',
  DICE = 'DICE',
  FRACTIONS = 'FRACTIONS',
  MATHIGON = 'MATHIGON',
  MATHSBOT = 'MATHSBOT',
  MATHSPAD = 'MATHSPAD',
  MATHWAY = 'MATHWAY',
  NUMWORKS = 'NUMWORKS',
  REKNEREK = 'REKNEREK',
  SPINNER = 'SPINNER',
  TEN_FRAMES = 'TEN_FRAMES',
  WOLFRAM_ALPHA = 'WOLFRAM_ALPHA',
  MORE_APPS = 'MORE_APPS',

  // General Apps
  BBC_BITESIZE = 'BBC_BITESIZE',
  CURIPOD = 'CURIPOD',
  GOOGLE_MAPS = 'GOOGLE_MAPS',
  SCRATCH = 'SCRATCH',
  WIKIPEDIA = 'WIKIPEDIA',
  YOUTUBE = 'YOUTUBE',
  AI_YOUTUBE = 'AI_YOUTUBE',

  // Science Apps
  HUMAN_ANATOMICAL_MODELS = 'HUMAN_ANATOMICAL_MODELS',
  BIODIGITAL = 'BIODIGITAL',
  CHEMIX = 'CHEMIX',
  PHET_SIMULATION = 'PHET_SIMULATION',
  PERIODIC_TABLE = 'PERIODIC_TABLE',
  AI_PHET_SIMULATION = 'AI_PHET_SIMULATION',

  // Literacy and Languages
  UFLI_BLENDING_BOARD = 'UFLI_BLENDING_BOARD',
  UFLI_WORD_WORK_MAT = 'UFLI_WORD_WORK_MAT',
  WORD_REFERENCE = 'WORD_REFERENCE',
  WIKTIONARY = 'WIKTIONARY',
  DICTIONARY = 'DICTIONARY',
  INTERVENTION_CARDS = 'INTERVENTION_CARDS',
  THESAURUS = 'THESAURUS',
  MINDFLOW = 'MINDFLOW',

  // Music Apps
  PIANO = 'PIANO',
  SONG_MAKER = 'SONG_MAKER',
  SOUNDTRAP = 'SOUNDTRAP',
  SPROUTBEAT = 'SPOUTBEAT',
  TRANSITIONSDJ = 'TRANSITIONSDJ',
  XYLOPHONE = 'XYLOPHONE',

  // Design
  FORMIT = 'FORMIT',
  SKETCHUP = 'SKETCHUP',
  CANVA = 'CANVA',

  // Advanced
  ONEDRIVE_PPT = 'ONEDRIVE_PPT',

  // Coding
  PICTOBLOX = 'PICTOBLOX',
  MBLOCK = 'MBLOCK',
  MACHINE_LEARNING_FOR_KIDS = 'MACHINE_LEARNING_FOR_KIDS',
  PLAYGROUND = 'PLAYGROUND',
  WEB_VIEWER_WEGGL = 'WEB_VIEWER_WEGGL',

  // Korean apps
  CODMOS = 'CODMOS',
  MATHFLAT = 'MATHFLAT',
  READINGN = 'READINGN',

  // visible to you apps
  VISIBLE_TO_YOU_PERIODIC_TABLE = 'VISIBLE_TO_YOU_PERIODIC_TABLE',
  VISIBLE_TO_YOU_CALCULATOR = 'VISIBLE_TO_YOU_CALCULATOR',
  VISIBLE_TO_YOU_GRAPHING_CALCULATOR = 'VISIBLE_TO_YOU_GRAPHING_CALCULATOR',

  REQUEST_AN_APP = 'REQUEST_AN_APP',
}

export enum AppInstanceType {
  IFRAME = 'IFRAME',
  WEBVIEWER = 'WEBVIEWER',
  MARIO = 'MARIO',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  ONEDRIVE = 'ONEDRIVE',
  ONEDRIVE_PPT = 'ONEDRIVE_PPT',
  CHAT_GPT = 'CHAT_GPT',
  PENCIL_AI = 'PENCIL_AI',
  PENCIL_AI_ASSISTANT = 'PENCIL_AI_ASSISTANT',
  CORE = 'CORE',
  VISIBLE_TO_YOU = 'VISIBLE_TO_YOU',
  REQUEST_AN_APP = 'REQUEST_AN_APP',
}

export interface IAppInformation {
  uid?: string;
  icon?: string;
  title: string;
  tag: string;
  instanceType: string;
  selector_icon?: string;
  disabledIcon?: string;
  description?: string;
  flag?: string;
  featureFlag?: string;
  width?: number;
  height?: number;
  close?: boolean;
  showMobile?: boolean;
  onlyHost?: boolean;
  disabled?: boolean;
  isLibraryAppParent?: boolean;
  actionIds?: string[];
  alwaysShow?: boolean;
  permissionName?: string;
  isCore?: boolean;
  appTiers?: SUBSCRIPTION_TYPES[];
  forceAvailableOnMobile?: boolean;
  iframeAppConfig?: IIframeAppConfig;
  webviewerAppConfig?: IWebviewerAppConfig;
  marioAppConfig?: IMarioAppConfig;
  modalConfig?: IAppModalInformation;
  gptAppConfig?: IGptAppConfig;
}

export interface IGptAppConfig {
  botId: string;
  icon: string;
  name: string;
  description: string;
}
export interface IIframeAppConfig {
  shareablePin?: boolean;
  sharedUrl?: string;
  defaultUrl?: string;
}

export interface IWebviewerAppConfig {
  defaultUrl?: string;
  webgl?: boolean;
  initialZoom?: number;
  country?: string;
  adblock?: boolean;
}

export interface IMarioAppConfig {
  mario_resource?: MarioResource;
  allowFullscreen?: boolean;
  hasYDocAccess?: boolean;
}

export interface IAppModalInformation {
  title: string;
  icon?: string;
  description?: string;
  stringIncludesValidation?: string;
  errorMessage?: string;
  buttonTitle?: string;
  queryParams?: Map<string, string>;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AppConfigurationService {
  private readonly coreAppSection: AppSectionsLocalized = {
    value: AppSections.CORE,
    label: this.translate.instant('Core'),
  };

  private readonly productivityAppSection: AppSectionsLocalized = {
    value: AppSections.PRODUCTIVITY,
    label: this.translate.instant('Productivity'),
  };

  private readonly aiAppSection: AppSectionsLocalized = {
    value: AppSections.AI,
    label: this.translate.instant('AI-Enabled Apps'),
  };

  private readonly educationAppSection: AppSectionsLocalized = {
    value: AppSections.EDUCATION,
    label: this.translate.instant('General Learning Tools'),
  };

  private readonly mathAppSection: AppSectionsLocalized = {
    value: AppSections.MATH,
    label: this.translate.instant('Numeracy and Math Tools'),
  };

  private readonly literacyAppSection: AppSectionsLocalized = {
    value: AppSections.LITERACY,
    label: this.translate.instant('Literacy and Language Tools'),
  };

  private readonly scienceAppSection: AppSectionsLocalized = {
    value: AppSections.SCIENCE,
    label: this.translate.instant('Science Tools'),
  };

  private readonly musicAppSection: AppSectionsLocalized = {
    value: AppSections.MUSIC,
    label: this.translate.instant('Music Tools'),
  };

  private readonly lmsAppSection: AppSectionsLocalized = {
    value: AppSections.LMS,
    label: this.translate.instant('Learning Portals - Account Required'),
  };

  private readonly designAppSection: AppSectionsLocalized = {
    value: AppSections.DESIGN,
    label: this.translate.instant('Design'),
  };

  private readonly gamesAppSection: AppSectionsLocalized = {
    value: AppSections.GAMES,
    label: this.translate.instant('Games'),
  };

  private readonly advancedAppSection: AppSectionsLocalized = {
    value: AppSections.ADVANCED,
    label: this.translate.instant('Advanced'),
  };

  private readonly codingAppSection: AppSectionsLocalized = {
    value: AppSections.CODING,
    label: this.translate.instant('Coding Tools'),
  };

  private readonly appsOnlyVisibleToYouAppSection: AppSectionsLocalized = {
    value: AppSections.APPS_ONLY_VISIBLE_TO_YOU,
    label: this.translate.instant('Apps only visible to you'),
  };

  private readonly requestAnAppAppSection: AppSectionsLocalized = {
    value: AppSections.REQUEST_AN_APP,
    label: this.translate.instant('Request an app'),
  };

  private _alwaysShowAppsList: IAppInformation[] = [
    {
      uid: CollaborativeApps.ANALYTICS,
      title: this.translate.instant('Analytics & Recordings'),
      instanceType: AppInstanceType.CORE,
      icon: 'analytics_apps',
      disabledIcon: 'analytics_apps_disabled',
      description: this.translate.instant('Access metrics and view Space recordings'),
      actionIds: ['toggleSessionAnalytics'],
      close: true,
      showMobile: false,
      tag: this.coreAppSection.value,
      onlyHost: true,
      flag: 'session_analytics_ui',
      appTiers: proFeatureSubscriptionTypes,
      isCore: true,
    },
    {
      uid: CollaborativeApps.BREAKOUT_ROOMS,
      title: this.translate.instant('Breakout Rooms'),
      instanceType: AppInstanceType.CORE,
      icon: 'breakout_rooms_apps',
      disabledIcon: 'breakout_rooms_apps_disabled',
      description: this.translate.instant('Break into groups for focused discussion'),
      actionIds: ['toggleBreakoutRoomsManagement'],
      close: true,
      showMobile: false,
      tag: this.coreAppSection.value,
      onlyHost: true,
      flag: 'breakout_rooms',
      appTiers: proFeatureSubscriptionTypes,
      isCore: true,
    },
    {
      uid: CollaborativeApps.COMMENTS,
      title: this.translate.instant('Commenting'),
      instanceType: AppInstanceType.CORE,
      icon: 'comments_apps',
      disabledIcon: 'comments_apps_disabled',
      description: this.translate.instant('Add comments to this Space'),
      actionIds: ['changeCommentsPanel'],
      close: false,
      showMobile: false,
      tag: this.coreAppSection.label,
      flag: 'enable_spaces_comments',
      isCore: true,
    },
  ];
  private _coreAppsList: IAppInformation[] = [
    this._alwaysShowAppsList[0],
    this._alwaysShowAppsList[1],
    {
      uid: CollaborativeApps.RESOURCE_LIBRARY,
      title: this.translate.instant('Resource Library'),
      instanceType: AppInstanceType.CORE,
      icon: 'apps_resource_library',
      description: this.translate.instant('Diagrams, equations, and popular objects'),
      actionIds: ['toggleMathsManipulatives'],
      close: false,
      showMobile: true,
      tag: this.coreAppSection.value,
      flag: 'enable_spaces_math_manipulatives',
      isCore: true,
    },
    {
      uid: CollaborativeApps.PENCIL_AI,
      title: this.translate.instant('AI Lesson Generator'),
      instanceType: AppInstanceType.PENCIL_AI,
      icon: 'ai_lesson_generator',
      disabledIcon: 'ai_lesson_generator_disabled',
      description: this.translate.instant('Generate lessons on whiteboard'),
      actionIds: ['pencilAiAction'],
      showMobile: true,
      tag: this.coreAppSection.value,
      onlyHost: true,
      flag: 'pencil_ai_assistant',
      appTiers: proFeatureSubscriptionTypes,
      isCore: true,
    },
    this._alwaysShowAppsList[2],
    {
      uid: CollaborativeApps.WEBVIEWER,
      icon: 'collaborative_browser',
      title: this.translate.instant('Web Viewer'),
      instanceType: AppInstanceType.WEBVIEWER,
      description: this.translate.instant('Collaborate over a shared browser'),
      actionIds: ['insert_additional_app_actions'],
      tag: this.coreAppSection.value,
      selector_icon: 'collaborative_browser',
      disabledIcon: 'collaborative_browser_disabled',
      flag: 'mario',
      permissionName: 'insertWebViewer',
      onlyHost: false,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
  ];

  private readonly requestAnApp: IAppInformation = {
    uid: CollaborativeApps.REQUEST_AN_APP,
    instanceType: AppInstanceType.VISIBLE_TO_YOU,
    title: this.translate.instant('Looking for an app?'),
    icon: 'app_request_fill',
    actionIds: ['requestApp'],
    showMobile: true,
    tag: this.requestAnAppAppSection.value,
    alwaysShow: true,
    description: this.translate.instant('Click to submit a feature request'),
    isCore: true,
  };

  private _defaultAppsList: IAppInformation[] = [
    ...this._coreAppsList,
    {
      uid: CollaborativeApps.CHAT_GPT,
      tag: this.aiAppSection.value,
      icon: 'app_chat_gpt',
      title: this.translate.instant('Chat GPT'),
      width: CHAT_GPT_APP_DEFAULT_WIDTH,
      height: CHAT_GPT_APP_DEFAULT_HEIGHT,
      instanceType: AppInstanceType.CHAT_GPT,
      flag: 'enable_chatgpt',
      description: this.translate.instant('Ask AI anything, together!'),
      actionIds: ['addChatGptApp', 'addChatGptAppToNewBoard', 'addChatGptAppAsPrivate'],
      showMobile: true,
      onlyHost: true,
      gptAppConfig: {
        botId: '641c981bbdeb6b4ba0df08f5',
        name: 'Chat with GPT',
        description: 'Ask AI anything, together!',
        icon: 'https://firebasestorage.googleapis.com/v0/b/pencil-frontend-staging.appspot.com/o/chat-with-gpt-icon.svg?alt=media&token=2d8f1fd4-b568-4ed2-8958-cfaadd78fde3',
      },
      appTiers: proFeatureSubscriptionTypes,
      isCore: true,
    },
    {
      uid: CollaborativeApps.IFRAME,
      icon: 'code_iframe',
      title: this.translate.instant('iFrame'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      flag: 'mario',
      featureFlag: 'enable_iframe',
      tag: this.advancedAppSection.value,
      selector_icon: 'code_iframe',
      description: this.translate.instant('Import an iFrame into Spaces'),
      modalConfig: {
        title: this.translate.instant('Add iFrame'),
        buttonTitle: this.translate.instant('Add iFrame'),
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.MARIO,
      icon: 'code_iframe',
      title: this.translate.instant('Mario App'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.advancedAppSection.value,
      selector_icon: 'code_iframe',
      flag: 'mario',
      featureFlag: 'enable_mario',
      description: this.translate.instant('Import a Mario App into Spaces'),
      modalConfig: {
        title: this.translate.instant('Add Mario App'),
        buttonTitle: this.translate.instant('Add Mario App'),
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.ONEDRIVE,
      icon: 'apps_onedrive',
      title: this.translate.instant('OneDrive'),
      instanceType: AppInstanceType.ONEDRIVE,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_onedrive',
      flag: 'mario',
      featureFlag: 'enable_onedrive_import',
      actionIds: ['onedrive_app_actions'],
      showMobile: true,
      onlyHost: true,
      description: this.translate.instant('Import from Microsoft OneDrive'),
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_DRIVE,
      icon: 'google_drive',
      title: this.translate.instant('Google Drive'),
      instanceType: AppInstanceType.GOOGLE_DRIVE,
      tag: this.productivityAppSection.value,
      selector_icon: 'google_drive',
      flag: 'mario',
      featureFlag: 'enable_gdrive_import',
      actionIds: ['google_drive_app_actions'],
      showMobile: true,
      onlyHost: true,
      description: this.translate.instant('Import from Google Drive'),
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_DOCS,
      icon: 'apps_google_docs',
      title: this.translate.instant('Google Docs'),
      instanceType: AppInstanceType.IFRAME,
      flag: 'mario',
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_google_docs',
      width: 1000,
      height: 1000,
      description: this.translate.instant('Import a Google Docs document'),
      modalConfig: {
        title: this.translate.instant('Import from Google Docs'),
        description: this.translate.instant(
          'Paste a link to a document from Google Docs here. This document will only be visible to those with access.',
        ),
        icon: 'google_docs',
        buttonTitle: this.translate.instant('Import'),
        stringIncludesValidation: 'docs.google.com/document',
        errorMessage: 'Please enter a valid Google Docs link - e.g., docs.google.com/document/XYZ',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_FORMS,
      icon: 'apps_google_forms',
      title: this.translate.instant('Google Forms'),
      instanceType: AppInstanceType.IFRAME,
      flag: 'mario',
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_google_forms',
      description: this.translate.instant('Import a Google Forms document'),
      modalConfig: {
        title: this.translate.instant('Import from Google Forms'),
        description: this.translate.instant(
          'Please paste a link to a form from Google Forms below. This form will only be visible to those with access.',
        ),
        icon: 'google_forms',
        buttonTitle: this.translate.instant('Import'),
        stringIncludesValidation: 'docs.google.com/forms',
        errorMessage: 'Please enter a valid Google Forms link - e.g., docs.google.com/forms/XYZ',
        queryParams: new Map([['embedded', 'true']]),
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_SHEETS,
      icon: 'apps_google_sheets',
      title: this.translate.instant('Google Sheets'),
      instanceType: AppInstanceType.IFRAME,
      flag: 'mario',
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_google_sheets',
      description: this.translate.instant('Import a Google Sheets document'),
      modalConfig: {
        title: this.translate.instant('Import from Google Sheets'),
        description: this.translate.instant(
          'Please paste a link to a spreadsheet from Google Sheets here. This spreadsheet will only be visible to those with access.',
        ),
        icon: 'google_sheets',
        buttonTitle: 'Import',
        stringIncludesValidation: 'docs.google.com/spreadsheets',
        errorMessage:
          'Please enter a valid Google Sheets link - e.g., docs.google.com/spreadsheets/XYZ',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_SLIDES,
      icon: 'apps_google_slides',
      title: this.translate.instant('Google Slides'),
      instanceType: AppInstanceType.IFRAME,
      flag: 'mario',
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_google_slides',
      description: this.translate.instant('Import a Google Slides document'),
      modalConfig: {
        title: this.translate.instant('Import from Google Slides'),
        description: this.translate.instant(
          'Please paste a link to a presentation from Google Slides below. This presentation will only be visible to those with access.',
        ),
        icon: 'google_slides',
        buttonTitle: this.translate.instant('Import'),
        stringIncludesValidation: 'docs.google.com/presentation',
        errorMessage:
          'Please enter a valid Google Slides link - e.g., docs.google.com/presentation/XYZ',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.POLLS,
      icon: 'mario_polls',
      title: this.translate.instant('Poll'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'mario_polls',
      width: 500,
      height: 550,
      flag: 'mario',
      description: this.translate.instant('Run an interactive poll in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.POLLS,
          major_version: 2,
        },
        allowFullscreen: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.STOPWATCH,
      icon: 'stopwatch_mario',
      title: this.translate.instant('Stopwatch'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'stopwatch_mario',
      width: 480,
      height: 295,
      flag: 'mario',
      description: this.translate.instant('Shared stopwatch in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.STOPWATCH,
          major_version: 1,
        },
        allowFullscreen: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.TIMER,
      icon: 'timer_mario',
      title: this.translate.instant('Timer'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'timer_mario',
      width: 480,
      height: 345,
      flag: 'mario',
      description: this.translate.instant('Shared timer in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.TIMER,
          major_version: 1,
        },
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.ERASER,
      icon: 'apps_eraser.png',
      title: this.translate.instant('Eraser.io'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_eraser.png',
      width: 1280,
      height: 720,
      flag: 'mario',
      description: this.translate.instant('Interactive flowcharts in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.ERASER,
          major_version: 1,
        },
        allowFullscreen: true,
      },
      disabled: true,
      isCore: true,
    },
    {
      uid: CollaborativeApps.ONEDRIVE_PPT,
      icon: 'apps_powerpoint.png',
      title: this.translate.instant('PowerPoint Presentation'),
      instanceType: AppInstanceType.ONEDRIVE_PPT,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_powerpoint.png',
      flag: 'mario',
      featureFlag: 'enable_onedrive_import',
      actionIds: ['onedrive_ppt_app_actions'],
      showMobile: true,
      onlyHost: true,
      description: this.translate.instant('Present interactive PowerPoints'),
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.YOUTUBE_MARIO,
      icon: 'apps_youtube.png',
      title: this.translate.instant('YouTube'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_youtube.png',
      width: MARIO_LARGE_WIDTH,
      height: MARIO_LARGE_HEIGHT,
      flag: 'mario',
      description: this.translate.instant('Watch videos together on YouTube'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.YOUTUBE_MARIO,
          major_version: 1,
        },
        allowFullscreen: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.AI_YOUTUBE,
      icon: 'apps_youtube.png',
      title: this.translate.instant('YouTube'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.productivityAppSection.value,
      selector_icon: 'apps_youtube.png',
      flag: 'mario',
      description: this.translate.instant('Watch videos together on YouTube'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.youtube.com/',
        adblock: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.HUMAN_ANATOMICAL_MODELS,
      icon: 'human_anatomical_models',
      title: this.translate.instant('Human anatomical models'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['toggleBioDigitalModels'],
      onlyHost: true,
      tag: this.scienceAppSection.value,
      selector_icon: 'human_anatomical_models',
      isLibraryAppParent: true,
      flag: 'mario',
      description: this.translate.instant('3D models for medical instruction'),
      webviewerAppConfig: {
        defaultUrl: 'https://human.biodigital.com/login?returnUrl=/explore',
        webgl: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.QUESTION_LIBRARY,
      icon: 'apps_library',
      title: this.translate.instant('Question Library'),
      instanceType: AppInstanceType.CORE,
      actionIds: ['toggleQuestionsLibrary'],
      tag: this.educationAppSection.value,
      isCore: true,
    },
    {
      uid: CollaborativeApps.BIODIGITAL,
      icon: 'apps_biodigital.png',
      title: this.translate.instant('BioDigital'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['toggleBioDigitalModels'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_biodigital.png',
      flag: 'mario',
      description: this.translate.instant('3D anatomical models'),
      webviewerAppConfig: {
        defaultUrl: 'https://human.biodigital.com/login?returnUrl=/explore',
        webgl: true,
      },
      disabled: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.BBC_BITESIZE,
      icon: 'apps_bbc_bitesize.png',
      title: this.translate.instant('BBC Bitesize'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_bbc_bitesize.png',
      flag: 'mario',
      description: this.translate.instant('Open BBC Bitesize in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.bbc.co.uk/bitesize',
        country: 'GB',
        webgl: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CURIPOD,
      icon: 'apps_curipod.png',
      title: this.translate.instant('Curipod'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_curipod.png',
      flag: 'mario',
      description: this.translate.instant('Share Curipod presentations'),
      webviewerAppConfig: {
        defaultUrl: 'https://curipod.com/lessons',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CHEMIX,
      icon: 'apps_chemix.png',
      title: this.translate.instant('Chemix'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.scienceAppSection.value,
      selector_icon: 'apps_chemix.png',
      flag: 'mario',
      description: this.translate.instant('Intractive STEM diagramming tool'),
      webviewerAppConfig: {
        defaultUrl: 'https://chemix.org/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.ABC_READING_EGGS,
      icon: 'apps_abc-reading-eggs.png',
      title: this.translate.instant('ABC Reading Eggs'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_abc-reading-eggs.png',
      flag: 'mario',
      description: this.translate.instant('Open ABC Reading Eggs in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.readingeggs.com/login',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.ACTIVELEARN,
      icon: 'apps_active_learn.png',
      title: this.translate.instant('ActiveLearn'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_active_learn.png',
      flag: 'mario',
      description: this.translate.instant('Open ActiveLearn in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.pearsonactivelearn.com/app/login',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.BLACKBOARD,
      icon: 'apps_blackboard.png',
      title: this.translate.instant('Blackboard'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_blackboard.png',
      flag: 'mario',
      description: this.translate.instant('Open Blackboard in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.blackboardconnect.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CANVAS_LMS,
      icon: 'apps_canvas_lms.png',
      title: this.translate.instant('Canvas LMS'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_canvas_lms.png',
      flag: 'mario',
      description: this.translate.instant('Open Canvas in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.instructure.com/canvas/login/free-for-teacher',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CLASSPAD,
      icon: 'apps_classpad.png',
      title: this.translate.instant('ClassPad'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_classpad.png',
      flag: 'mario',
      description: this.translate.instant('Collaborative Math canvas'),
      webviewerAppConfig: {
        defaultUrl: 'https://classpad.net/note/#/tools/math',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.DESMOS_GRAPHING_CALCULATOR,
      icon: 'apps_desmos_calculator',
      title: this.translate.instant('Desmos Graphing Calculator'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_desmos_calculator',
      flag: 'mario',
      description: this.translate.instant('Collaborative graphing calculator'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.desmos.com/calculator',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.DESMOS_GEOMETRY,
      icon: 'apps_desmos_geometry',
      title: this.translate.instant('Desmos Geometry'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_desmos_geometry',
      flag: 'mario',
      description: this.translate.instant('Collaborative geometry / polygon tools'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.desmos.com/geometry',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.DESMOS_SCIENTIFIC_CALCULATOR,
      icon: 'apps_desmos_scientific',
      title: this.translate.instant('Desmos Scientific Calculator'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_desmos_scientific',
      flag: 'mario',
      description: this.translate.instant('Collaborative scientific calculator'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.desmos.com/scientific',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_CLASSROOM_LMS,
      icon: 'apps_google_classroom.png',
      title: this.translate.instant('Google Classroom'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_google_classroom.png',
      flag: 'mario',
      description: this.translate.instant('Open Classroom in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://classroom.google.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.IREADY,
      icon: 'apps_iready.png',
      title: this.translate.instant('i-Ready'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_iready.png',
      flag: 'mario',
      description: this.translate.instant('Open i-Ready in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://login.i-ready.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.IXL,
      icon: 'apps_ixl.png',
      title: this.translate.instant('IXL'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_ixl.png',
      flag: 'mario',
      description: this.translate.instant('Open IXL in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.ixl.com/signin',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHIGON,
      icon: 'apps_mathigon.png',
      title: this.translate.instant('Mathigon Polypad'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_mathigon.png',
      flag: 'mario',
      description: this.translate.instant('Interactive math widgets'),
      webviewerAppConfig: {
        defaultUrl: 'https://mathigon.org/polypad',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHSBOT,
      icon: 'apps_mathsbot.png',
      title: this.translate.instant('MathsBot'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_mathsbot.png',
      flag: 'mario',
      description: this.translate.instant('Interactive maths widgets'),
      webviewerAppConfig: {
        defaultUrl: 'https://mathsbot.com/toolMenu',
        webgl: false,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHSPAD,
      icon: 'apps_mathspad.png',
      title: this.translate.instant('MathsPad'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_mathspad.png',
      flag: 'mario',
      description: this.translate.instant('Interactive maths widgets'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.mathspad.co.uk/resources.php?interactives=1',
        webgl: false,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHWAY,
      icon: 'apps_mathway.png',
      title: this.translate.instant('Mathway'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_mathway.png',
      flag: 'mario',
      description: this.translate.instant('Collaborative algebra problem solver'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.mathway.com/Algebra',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.PERIODIC_TABLE,
      icon: 'apps_periodic',
      title: this.translate.instant('Periodic Table'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.scienceAppSection.value,
      selector_icon: 'apps_periodic',
      flag: 'mario',
      description: this.translate.instant('Collaborative periodic table'),
      webviewerAppConfig: {
        defaultUrl: 'https://ptable.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.PHET_SIMULATION,
      icon: 'apps_stem.svg',
      title: this.translate.instant('STEM Simulations'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['togglePhetSimulations'],
      onlyHost: true,
      tag: this.scienceAppSection.value,
      selector_icon: 'apps_stem.svg',
      isLibraryAppParent: true,
      flag: 'mario',
      description: this.translate.instant('Interactive simulations to teach STEM concepts'),
      webviewerAppConfig: {
        defaultUrl: 'https://phet.colorado.edu/en/simulations/filter?type=html,prototype',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.AI_PHET_SIMULATION,
      icon: 'apps_stem.svg',
      title: this.translate.instant('STEM Interactive Simulations'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.scienceAppSection.value,
      selector_icon: 'apps_stem.svg',
      isLibraryAppParent: true,
      flag: 'mario',
      description: this.translate.instant('Interactive simulations to teach STEM concepts'),
      webviewerAppConfig: {
        defaultUrl: 'https://phet.colorado.edu/en/simulations/filter?type=html,prototype',
      },
      disabled: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.GOOGLE_MAPS,
      icon: 'apps_google_maps.png',
      title: this.translate.instant('Google Maps'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_google_maps.png',
      flag: 'mario',
      description: this.translate.instant('Collaboratively browse Google Maps'),
      webviewerAppConfig: {
        defaultUrl: 'https://maps.google.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.KAHOOT,
      icon: 'apps_kahoot.png',
      title: this.translate.instant('Kahoot!'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_kahoot.png',
      width: MARIO_LARGE_WIDTH,
      height: MARIO_LARGE_HEIGHT,
      flag: 'mario',
      description: this.translate.instant('Run a Kahoot exercise in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.MARIO_IFRAME,
          major_version: 1,
          subpath: {
            subpathName: CollaborativeApps.KAHOOT,
            subpathRoute: 'kahoot',
          },
        },
        allowFullscreen: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.NEARPOD,
      icon: 'apps_nearpod.png',
      title: this.translate.instant('Nearpod'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_nearpod.png',
      width: MARIO_LARGE_WIDTH,
      height: MARIO_LARGE_HEIGHT,
      flag: 'mario',
      description: this.translate.instant('Run Nearpod quizzes in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.MARIO_IFRAME,
          major_version: 1,
          subpath: {
            subpathName: CollaborativeApps.NEARPOD,
            subpathRoute: 'nearpod',
          },
        },
        allowFullscreen: true,
      },
      modalConfig: {
        title: this.translate.instant('Nearpod'),
        buttonTitle: this.translate.instant('Join'),

        stringIncludesValidation: 'nearpod',
        errorMessage: 'Please enter a valid Nearpod link',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.QUIZLET,
      icon: 'apps_quizlet.png',
      title: this.translate.instant('Quizlet'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_quizlet.png',
      width: MARIO_LARGE_WIDTH,
      height: MARIO_LARGE_HEIGHT,
      flag: 'mario',
      description: this.translate.instant('Run Quizlet sessions in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.MARIO_IFRAME,
          major_version: 1,
          subpath: {
            subpathName: CollaborativeApps.QUIZLET,
            subpathRoute: 'quizlet',
          },
        },
        allowFullscreen: true,
      },
      modalConfig: {
        title: this.translate.instant('Quizlet'),
        buttonTitle: this.translate.instant('Join'),
        stringIncludesValidation: 'quizlet',
        errorMessage: 'Please enter a valid Quizlet link',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.QUIZIZZ,
      icon: 'apps_quizizz.png',
      title: this.translate.instant('Quizizz'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_quizizz.png',
      width: MARIO_LARGE_WIDTH,
      height: MARIO_LARGE_HEIGHT,
      flag: 'mario',
      description: this.translate.instant('Run Quizizz quizzes in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.MARIO_IFRAME,
          major_version: 1,
          subpath: {
            subpathName: CollaborativeApps.QUIZIZZ,
            subpathRoute: 'quizizz',
          },
        },
        allowFullscreen: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.SCRATCH,
      icon: 'apps_scratch.png',
      title: this.translate.instant('Scratch'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.codingAppSection.value,
      selector_icon: 'apps_scratch.png',
      flag: 'mario',
      description: this.translate.instant('Learn programming the fun way!'),
      webviewerAppConfig: {
        defaultUrl: 'https://scratch.mit.edu/projects/editor/',
        webgl: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MINDFLOW,
      icon: 'apps_mindflow.png',
      title: this.translate.instant('MindFlow Speed Reading'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_mindflow.png',
      flag: 'mario',
      description: this.translate.instant('Accelerate reading speeds with MindFlow'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.mindflowspeedreading.com/pencilSpacesLogin',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.DICTIONARY,
      icon: 'apps_dictionary.com.png',
      title: this.translate.instant('Dictionary.com'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_dictionary.com.png',
      flag: 'mario',
      description: this.translate.instant('Search words on Dictionary.com'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.dictionary.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.INTERVENTION_CARDS,
      icon: 'app_intervention_cards.png',
      title: this.translate.instant('Intervention Cards'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'app_intervention_cards.png',
      flag: 'mario',
      description: this.translate.instant('Amplify Intervention Cards for literacy'),
      webviewerAppConfig: {
        defaultUrl: 'https://interventioncards.amplify.com/',
        initialZoom: 0.8,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.THESAURUS,
      icon: 'apps_thesaurus.com.png',
      title: this.translate.instant('Thesaurus.com'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_thesaurus.com.png',
      flag: 'mario',
      description: this.translate.instant('Search words on Thesaurus.com'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.thesaurus.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.UFLI_BLENDING_BOARD,
      icon: 'apps_ufli.png',
      title: this.translate.instant('UFLI Blending Board'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_ufli.png',
      flag: 'mario',
      description: this.translate.instant('Interactive tool to improve reading skills'),
      webviewerAppConfig: {
        defaultUrl: 'https://research.dwi.ufl.edu/op.n/file/bca9ju45kvvrvoan',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.UFLI_WORD_WORK_MAT,
      icon: 'apps_ufli.png',
      title: this.translate.instant('UFLI Word Work Mat'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_ufli.png',
      flag: 'mario',
      description: this.translate.instant('Interactive tool to improve reading skills'),
      webviewerAppConfig: {
        defaultUrl: 'https://research.dwi.ufl.edu/op.n/file/gc8nkxns914enc7d',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WIKIPEDIA,
      icon: 'apps_wikipedia.png',
      title: this.translate.instant('Wikipedia'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_wikipedia.png',
      flag: 'mario',
      description: this.translate.instant('The world’s largest free encyclopedia'),
      webviewerAppConfig: {
        defaultUrl: 'https://wikipedia.org/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WIKTIONARY,
      icon: 'apps_wiktionary.png',
      title: this.translate.instant('Wiktionary'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_wiktionary.png',
      flag: 'mario',
      description: this.translate.instant('Multilingual free dictionary'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.wiktionary.org/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WORD_REFERENCE,
      icon: 'apps_word_reference.png',
      title: this.translate.instant('Word Reference'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.literacyAppSection.value,
      selector_icon: 'apps_word_reference.png',
      flag: 'mario',
      description: this.translate.instant('Multilingual dictionary'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.wordreference.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHIA,
      icon: 'apps_mathia.png',
      title: this.translate.instant('MATHia'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_mathia.png',
      flag: 'mario',
      description: "Open Carnegie Learning's MATHia",
      webviewerAppConfig: {
        defaultUrl: 'https://online.carnegielearning.com',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MYMATHS,
      icon: 'apps_mymaths.png',
      title: this.translate.instant('MyMaths'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_mymaths.png',
      flag: 'mario',
      description: this.translate.instant('Open MyMaths in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://login.mymaths.co.uk/login',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.OFF2CLASS,
      icon: 'apps_off2class.png',
      title: this.translate.instant('Off2Class'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_off2class.png',
      flag: 'mario',
      description: this.translate.instant('Open Off2Class in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.off2class.com/login',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.XCELLATE,
      icon: 'apps_xcellate.png',
      title: this.translate.instant('Xcellate'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_xcellate.png',
      flag: 'mario',
      description: this.translate.instant('Open Xcellate in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.xcellate.co.za/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      disabled: true,
      isCore: true,
    },
    {
      uid: CollaborativeApps.GEOGEBRA,
      icon: 'app_geogebra.png',
      title: this.translate.instant('GeoGebra'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'app_geogebra.png',
      flag: 'mario',
      description: this.translate.instant('Open GeoGebra in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.geogebra.org/calculator',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WORDWALL,
      icon: 'app_wordwall.png',
      title: this.translate.instant('Wordwall'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'app_wordwall.png',
      flag: 'mario',
      description: this.translate.instant('Open Wordwall in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://wordwall.net/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.READWORKS,
      icon: 'apps_readworks.png',
      title: 'ReadWorks',
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.lmsAppSection.value,
      selector_icon: 'apps_readworks.png',
      flag: 'mario',
      description: this.translate.instant('Open ReadWorks in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.readworks.org/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.AVALON,
      icon: 'apps_avalon.png',
      title: this.translate.instant('Avalon'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_avalon.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Avalon directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://netgames.io/games/avalon/',
        shareablePin: true,
        sharedUrl: 'https://netgames.io/games/avalon/rooms/PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.CODE_WORDS,
      icon: 'apps_code_words.png',
      title: this.translate.instant('Codewords'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_code_words.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Codewords directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://codenames.game/',
        shareablePin: true,
        sharedUrl: 'https://codenames.game/rooms/PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.COLONIST,
      icon: 'apps_colonist.png',
      title: this.translate.instant('Colonist.io'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_colonist.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Catan directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://colonist.io/',
        shareablePin: false,
        sharedUrl: 'https://colonist.io/#PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.DOWN_FOR_A_CROSS,
      icon: 'apps_down_for_a_cross',
      title: this.translate.instant('DownForACross'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_down_for_a_cross',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Solve Crosswords directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://downforacross.com/',
        shareablePin: false,
        sharedUrl: 'https://downforacross.com/beta/game/PENCIL_PIN_CODE',
      },
      disabled: true,
      isCore: true,
    },
    {
      uid: CollaborativeApps.DRAW_BATTLE,
      icon: 'apps_draw_battle.png',
      title: this.translate.instant('DrawBattle.io'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_draw_battle.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Draw Battle directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://drawbattle.io/',
        shareablePin: true,
        sharedUrl: 'https://drawbattle.io/PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.LI_POKER,
      icon: 'apps_li_poker.png',
      title: this.translate.instant('LiPoker'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_li_poker.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Poker directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://lipoker.io/game/',
        shareablePin: false,
        sharedUrl: 'https://lipoker.io/game/PENCIL_PIN_CODE',
      },
      disabled: true,
      isCore: true,
    },
    {
      uid: CollaborativeApps.SCATTEGORIES,
      icon: 'apps_scattegories',
      title: this.translate.instant('Scattegories'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_scattegories',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Scattegories directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://really.boring.website/',
        shareablePin: true,
        sharedUrl: 'https://really.boring.website/room/PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.SKRIBBLE_IO,
      icon: 'apps_skribbleio.png',
      title: this.translate.instant('Skribbl.io'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_skribbleio.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Skribbl directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://skribbl.io/',
        shareablePin: false,
        sharedUrl: 'https://skribbl.io/?PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.TOP_FOUR,
      icon: 'apps_top_four.png',
      title: this.translate.instant('TopFour.io'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_top_four.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Top Four directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://topfour.io/',
        shareablePin: true,
        sharedUrl: 'https://topfour.io/PENCIL_PIN_CODE/players',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.WEREWOLF,
      icon: 'apps_werewolf.png',
      title: this.translate.instant('Werewolf'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_werewolf.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play Werewolf directly in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://onmuga.com/werewolf/',
        shareablePin: true,
        sharedUrl: 'https://onmuga.com/werewolf/PENCIL_PIN_CODE',
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.WORDLE,
      icon: 'apps_wordle.png',
      title: this.translate.instant('Wordle'),
      instanceType: AppInstanceType.IFRAME,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.gamesAppSection.value,
      selector_icon: 'apps_wordle.png',
      flag: 'mario',
      featureFlag: 'enable_iframe',
      description: this.translate.instant('Play collaborative Wordle in Spaces'),
      iframeAppConfig: {
        defaultUrl: 'https://onmuga.com/wordle/',
        sharedUrl: 'https://onmuga.com/wordle/PENCIL_PIN_CODE',
        shareablePin: true,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.SONG_MAKER,
      icon: 'apps_songmaker.png',
      title: this.translate.instant('Song Maker'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_songmaker.png',
      flag: 'mario',
      description: this.translate.instant('Create simple tunes in Spaces!'),
      webviewerAppConfig: {
        defaultUrl: 'https://musiclab.chromeexperiments.com/Song-Maker/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.SOUNDTRAP,
      icon: 'apps_soundtrap.png',
      title: this.translate.instant('Soundtrap'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_soundtrap.png',
      flag: 'mario',
      description: this.translate.instant('Make music together online'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.soundtrap.com/home/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.SPROUTBEAT,
      icon: 'apps_sproutbeat.png',
      title: this.translate.instant('SproutBeat'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_sproutbeat.png',
      flag: 'mario',
      description: this.translate.instant('Collaborative music resources'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.sproutbeat.com/login',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.TRANSITIONSDJ,
      icon: 'apps_transitions_dj.png',
      title: this.translate.instant('Transitions DJ'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_transitions_dj.png',
      flag: 'mario',
      description: this.translate.instant('Mix DJ mixes collaboratively!'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.transitions.dj/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.XYLOPHONE,
      icon: 'apps_xylophone.png',
      title: this.translate.instant('Xylophone'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_xylophone.png',
      flag: 'mario',
      description: this.translate.instant('Shared xylophone in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://playxylo.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.PIANO,
      icon: 'apps_piano_icon.png',
      title: this.translate.instant('Piano'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.musicAppSection.value,
      selector_icon: 'apps_piano_icon.png',
      flag: 'mario',
      description: this.translate.instant('Shared piano in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://musiclab.chromeexperiments.com/Shared-Piano/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.DICE_MARIO,
      icon: 'dice-mario',
      title: this.translate.instant('Dice'),
      instanceType: AppInstanceType.MARIO,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'dice-mario',
      flag: 'mario',
      description: this.translate.instant('Shared 6-sided dice in Spaces'),
      marioAppConfig: {
        mario_resource: {
          _type: 'DEPLOYED',
          name: CollaborativeApps.DICE_MARIO,
          major_version: 1,
        },
        allowFullscreen: false,
      },
      isCore: true,
    },
    {
      uid: CollaborativeApps.FRACTIONS,
      icon: 'apps_fractions.jpeg',
      title: this.translate.instant('Fractions'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_fractions.jpeg',
      flag: 'mario',
      description: this.translate.instant('Teach fractions interactively'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.didax.com/apps/fraction-number-line/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.NUMWORKS,
      icon: 'apps_numworks.png',
      title: this.translate.instant('NumWorks'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_numworks.png',
      flag: 'mario',
      description: this.translate.instant('College Board approved calculator'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.numworks.com/simulator/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.REKNEREK,
      icon: 'apps_reknerek.jpeg',
      title: this.translate.instant('Reknerek'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_reknerek.jpeg',
      flag: 'mario',
      description: this.translate.instant('Shared reknerek in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.didax.com/apps/rekenrek/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.SPINNER,
      icon: 'apps_spinner.jpeg',
      title: this.translate.instant('Spinner'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_spinner.jpeg',
      flag: 'mario',
      description: this.translate.instant('Shared spinner in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.didax.com/apps/spinners/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.TEN_FRAMES,
      icon: 'apps_tenframes.jpeg',
      title: this.translate.instant('Ten Frames'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_tenframes.jpeg',
      flag: 'mario',
      description: this.translate.instant('Teach numeracy with Ten Frames'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.didax.com/apps/ten-frames/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WOLFRAM_ALPHA,
      icon: 'apps_wolfram_alpha.png',
      title: this.translate.instant('Wolfram Alpha'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_wolfram_alpha.png',
      flag: 'mario',
      description: this.translate.instant('Interactive knowledge engine'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.wolframalpha.com/',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MORE_APPS,
      icon: 'apps_mathcenter.png',
      title: this.translate.instant('Math Learning Center'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.mathAppSection.value,
      selector_icon: 'apps_mathcenter.png',
      flag: 'mario',
      description: this.translate.instant('A collection of interactive numeracy tools'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.mathlearningcenter.org/apps',
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.FORMIT,
      icon: 'apps_formit.png',
      title: this.translate.instant('Autodesk FormIt'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      tag: this.designAppSection.value,
      selector_icon: 'apps_formit.png',
      flag: 'mario',
      description: this.translate.instant('Open Autodesk FormIt in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.formit.autodesk.com/',
        webgl: true,
      },
      onlyHost: true,
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.SKETCHUP,
      icon: 'apps_sketchup.png',
      title: this.translate.instant('SketchUp'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      tag: this.designAppSection.value,
      selector_icon: 'apps_sketchup.png',
      flag: 'mario',
      description: this.translate.instant('Open SketchUp in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://app.sketchup.com/app?hl=en',
        webgl: true,
      },
      onlyHost: true,
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CANVA,
      icon: 'apps_canva.png',
      title: 'Canva',
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      tag: this.designAppSection.value,
      selector_icon: 'apps_canva.png',
      flag: 'mario',
      description: this.translate.instant('Open Canva in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.canva.com/',
        webgl: true,
      },
      onlyHost: true,
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.CODMOS,
      icon: 'apps_codmos.png',
      title: this.translate.instant('CODMOS'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_codmos.png',
      flag: 'mario',
      description: this.translate.instant('Open CODMOS in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.codmos.io',
        webgl: false,
      },
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MATHFLAT,
      icon: 'apps_mathflat.png',
      title: this.translate.instant('MathFlat'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_mathflat.png',
      flag: 'mario',
      description: this.translate.instant('Open Mathflat in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.mathflat.com/',
        webgl: false,
      },
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.READINGN,
      icon: 'apps_readingn.png',
      title: this.translate.instant('ReadingN'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.educationAppSection.value,
      selector_icon: 'apps_readingn.png',
      flag: 'mario',
      description: this.translate.instant('Open ReadingN in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://www.readingn.com/',
        webgl: false,
      },
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.PICTOBLOX,
      icon: 'apps_pictoblox.png',
      title: 'PictoBlox',
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.codingAppSection.value,
      selector_icon: 'apps_pictoblox.png',
      flag: 'mario',
      description: this.translate.instant('Open PictoBlox in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://pictoblox.ai/',
        webgl: true,
      },
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MBLOCK,
      icon: 'apps_mblock.png',
      title: 'mBlock',
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.codingAppSection.value,
      selector_icon: 'apps_mblock.png',
      flag: 'mario',
      description: this.translate.instant('Open mBlock in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://ide.mblock.cc/',
        webgl: true,
      },
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.MACHINE_LEARNING_FOR_KIDS,
      icon: 'apps_scratch.png',
      title: this.translate.instant('Machine Learning for Kids'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.codingAppSection.value,
      selector_icon: 'apps_scratch.png',
      flag: 'mario',
      description: this.translate.instant('Open Machine Learning for Kids in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://machinelearningforkids.co.uk/scratch/',
        webgl: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.PLAYGROUND,
      icon: 'apps_machinelearningforkids.png',
      title: 'Playground',
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      onlyHost: true,
      tag: this.codingAppSection.value,
      selector_icon: 'apps_machinelearningforkids.png',
      flag: 'mario',
      description: this.translate.instant('Open Playground in Spaces'),
      webviewerAppConfig: {
        defaultUrl: 'https://playground.raise.mit.edu/create/',
        webgl: true,
      },
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.WEB_VIEWER_WEGGL,
      icon: 'collaborative_browser',
      title: this.translate.instant('Web Viewer+'),
      instanceType: AppInstanceType.WEBVIEWER,
      actionIds: ['insert_additional_app_actions'],
      tag: this.advancedAppSection.value,
      selector_icon: 'collaborative_browser',
      disabledIcon: 'collaborative_browser_disabled',
      flag: 'mario',
      featureFlag: 'enable_browser',
      permissionName: 'insertWebViewer',
      description: this.translate.instant('Web viewer with WebGL features'),
      webviewerAppConfig: {
        webgl: true,
      },
      onlyHost: true,
      showMobile: true,
      appTiers: [SUBSCRIPTION_TYPES.ENTERPRISE, SUBSCRIPTION_TYPES.PAID],
      isCore: true,
    },
    {
      uid: CollaborativeApps.VISIBLE_TO_YOU_PERIODIC_TABLE,
      instanceType: AppInstanceType.VISIBLE_TO_YOU,
      title: this.translate.instant('Periodic Table'),
      icon: 'apps_periodic_table',
      actionIds: ['togglePeriodicTable'],
      close: true,
      showMobile: false,
      tag: this.appsOnlyVisibleToYouAppSection.value,
      flag: 'wb_gadgets',
      description: this.translate.instant('Open a companion periodic table'),
      isCore: true,
    },
    {
      uid: CollaborativeApps.VISIBLE_TO_YOU_CALCULATOR,
      title: this.translate.instant('Calculator'),
      instanceType: AppInstanceType.VISIBLE_TO_YOU,
      icon: 'apps_calculator',
      actionIds: ['toggleCalculator'],
      close: true,
      showMobile: true,
      tag: this.appsOnlyVisibleToYouAppSection.value,
      flag: 'wb_gadgets',
      description: this.translate.instant('Basic and scientific calculator'),
      isCore: true,
    },
    {
      uid: CollaborativeApps.VISIBLE_TO_YOU_GRAPHING_CALCULATOR,
      instanceType: AppInstanceType.VISIBLE_TO_YOU,
      title: this.translate.instant('Graphing Calculator'),
      icon: 'apps_graphing_calculator',
      actionIds: ['toggleGraphCalculator'],
      close: true,
      showMobile: true,
      tag: this.appsOnlyVisibleToYouAppSection.value,
      flag: 'wb_gadgets',
      description: this.translate.instant('Plot simple or complex functions'),
      isCore: true,
    },
    this.requestAnApp,
  ];

  private _cachedDynamicAppsList: IAppInformation[] | null = null;
  private _cachedActiveSpaceId: string | undefined = undefined;

  constructor(
    private translate: TranslateService,
    private featureManagementService: FeatureManagementService,
    private flagsService: FlagsService,
    private spaceRepository: SpaceRepository,
  ) {
    this.spaceRepository.activeSpaceId$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((activeSpace) => {
        if (!activeSpace) {
          // clear cached apps when exiting the space
          this.clearCachedAppsList();
        }
      });
  }

  public getAppByUid(
    uid: CollaborativeApps | string,
    searchBackendAssignedAppsOnly = false,
  ): IAppInformation | undefined {
    if (searchBackendAssignedAppsOnly) {
      return this.dynamicAppsList.find((app) => app.uid === uid);
    }

    return (
      this.dynamicAppsList.find((app) => app.uid === uid) ??
      this._defaultAppsList.find((app) => app.uid === uid)
    );
  }

  get dynamicAppsList(): IAppInformation[] {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_BACKEND_APPS_LOADING)) {
      return this._defaultAppsList;
    }

    const activeSpace = this.spaceRepository.activeSpace;

    if (!activeSpace) {
      this.clearCachedAppsList();
      return this._defaultAppsList;
    }

    if (this._cachedDynamicAppsList && this._cachedActiveSpaceId === activeSpace._id) {
      return this._cachedDynamicAppsList;
    }

    this._cachedActiveSpaceId = activeSpace._id;

    if (activeSpace.coreApps?.length) {
      this._cachedDynamicAppsList = [
        ...this._alwaysShowAppsList,
        ...activeSpace.coreApps,
        this.requestAnApp,
      ];
    } else if (activeSpace.institution?.apps?.length) {
      this._cachedDynamicAppsList = [
        ...this._alwaysShowAppsList,
        ...activeSpace.institution.apps,
        this.requestAnApp,
      ];
    } else {
      this._cachedDynamicAppsList = this._defaultAppsList;
    }

    this._cachedDynamicAppsList = this._cachedDynamicAppsList.map((app) => {
      app.appTiers = !app.appTiers?.length ? allFeatureSubscriptionTypes : app.appTiers;
      return app;
    });

    return this._cachedDynamicAppsList;
  }

  public clearCachedAppsList(): void {
    if (!this._cachedDynamicAppsList) {
      return;
    }
    this._cachedDynamicAppsList = null;
    this._cachedActiveSpaceId = undefined;
  }

  /**
   * Updates the disabled status for the YouTube apps in the dynamic apps list based on WebViewer capability then returns the enabled app.
   * - Disables the Mario YouTube app when WebViewer is enabled
   * - Disables the WebViewer YouTube app when WebViewer is disabled
   * - If Backend apps loading is enabled, youtube apps are considered only if they are assigned to the institution
   *
   * @returns {Observable<IAppInformation>} An observable that emits the active YouTube app.
   */
  public setYoutubeApp$(): Observable<IAppInformation | undefined> {
    const isBackendAppsLoadingEnabled = this.flagsService.isFlagEnabled(
      FLAGS.ENABLE_BACKEND_APPS_LOADING,
    );

    return this.spaceRepository.activeSpaceId$.pipe(
      filterNil(),
      switchMap(() => {
        const marioYoutubeApp = this.getAppByUid(
          CollaborativeApps.YOUTUBE_MARIO,
          isBackendAppsLoadingEnabled,
        );
        const webViewerYoutubeApp = this.getAppByUid(
          CollaborativeApps.AI_YOUTUBE,
          isBackendAppsLoadingEnabled,
        );

        if (!marioYoutubeApp && !webViewerYoutubeApp) {
          return of(undefined);
        }

        return this.featureManagementService.isWebViewerEnabled$.pipe(
          map((isWebViewerEnabled) => {
            if (isWebViewerEnabled && webViewerYoutubeApp && !webViewerYoutubeApp.disabled) {
              if (marioYoutubeApp) {
                marioYoutubeApp.disabled = true;
              }
              return webViewerYoutubeApp;
            }
            if (webViewerYoutubeApp) {
              webViewerYoutubeApp.disabled = true;
            }
            return marioYoutubeApp;
          }),
        );
      }),
    );
  }
}

export const LegacyApps = [CollaborativeApps.BIODIGITAL];
